import { Card, CardPreview, Title3, Button, Text, Field, Label, TableRow, Table, Tooltip } from "@fluentui/react-components";
import { DocumentRegular, AttachRegular, EditRegular, LinkFilled } from "@fluentui/react-icons";
import { FC, useRef, useState, useEffect } from "react";
import { convertDocxToText } from "../../../services/docx-converter";
import { ContractField, Question, Tendering } from "../../../models/tendering";
import { deepCloneObject } from "../../../utils/clone";
import LinkQuestionsDialog from "../../dialogs/LinkQuestionsDialog";
import fileService from "../../../services/fileService";
import { updateTenderingDocument } from "../../../services/tenderingService";

interface TenderingContractProps {
  tenderingDocument?: any;
  updateTenderingData: (data: any) => void;
}

export const TenderingContract: FC<TenderingContractProps> = ({ 
  tenderingDocument, 
  updateTenderingData 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [contractFields, setContractFields] = useState<ContractField[]>(tenderingDocument?.properties?.contract?.fields || []);
  const [isLinkQuestionsDialogOpen, setIsLinkQuestionsDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<ContractField | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    let text: string = "";
    if (file) {
      try {
        text = await convertDocxToText(file);
        
      } catch (error) {
        console.error('Error converting DOCX:', error);
      }
      finally{
        const regexPattern = '{\\s*[^}]*\\s*}';
        const regex = new RegExp(regexPattern, 'gi');
        
        const matches =  text.match(regex);

        if(matches){
          matches.forEach(m => {
            const fieldName = m.slice(1, -1);
            
            var defaultField: ContractField = {
              fieldName: fieldName,
              fieldType: 'text',
              isDisabled: false,
              isDefaultField: false,
            }
            
            if(!contractFields.map(cf => cf.fieldName).includes(fieldName)){
              setContractFields(prevFields => [...prevFields, defaultField]);
              updateTenderingData((prevData: Tendering) => ({
                ...prevData,
                properties: {
                  ...prevData.properties,
                  contract: {
                    ...prevData.properties.contract,
                    fields: deepCloneObject(contractFields)
                  }
                }
              }));
            }
          })
        }
        
        setSelectedFile(file);
      }
    }
  };

  const uploadFile = async () => {
    console.log("Uploading file...");
    if(selectedFile){
      const renamedFile = new File([selectedFile], sanitizeFileName(selectedFile.name), { type: selectedFile.type });
      const formData = new FormData();
      formData.append('file', renamedFile);
      formData.append('displayFileName', selectedFile.name);
      formData.append('moduleID', tenderingDocument.tenderingID.toString());
      setIsUploading(true);

      await fileService.createAttachmentFile(formData).then(async response => {
        let newTendering: Tendering = deepCloneObject(tenderingDocument);
        newTendering.properties.contract = {
          ...newTendering.properties.contract,
          template: response.data.fileID
        }
        setIsUploading(false);
        updateTenderingData(newTendering);
        await updateTenderingDocument(tenderingDocument.tenderingID, newTendering);
      })
    } 
  }

  const sanitizeFileName = (name: string) => {
    return name.replace(/[^\x00-\x7F]/g, "_");
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    updateTenderingData({ 
      ...tenderingDocument, 
      contract: null
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleQuestionLinked = (field: ContractField, question: Question | null) => {
    // Update the field with the linked question
    const updatedFields = contractFields.map(cf => 
      cf.fieldName === field.fieldName 
        ? { ...cf, questionID: question?.questionID || null }
        : cf
    );
    
    setContractFields(updatedFields);
    
    // Update the tendering data
    updateTenderingData((prevData: Tendering) => ({
      ...prevData,
      properties: {
        ...prevData.properties,
        contract: {
          ...prevData.properties.contract,
          fields: updatedFields
        }
      }
    }));
    
    console.log(`Field "${field.fieldName}" linked to question:`, question);
  };

  useEffect(() => {
    if (selectedFile) {
      uploadFile();
    }
  }, [selectedFile]);

  return (
    <>
      <Card>
        <CardPreview className="p-4">
          <Title3>Avtal</Title3>
        </CardPreview>
        <div className="mt-4 space-y-4 overflow-y-auto p-4">
          <Field>
            <Label>Ladda upp avtal</Label>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            {/* Upload button or file display */}
            {!selectedFile ? (
              <Button
                appearance="outline"
                icon={<AttachRegular />}
                onClick={handleUploadClick}
                disabled={isUploading}
              >
                Välj fil
              </Button>
            ) : (
              <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md bg-gray-50">
                <div className="flex items-center gap-2">
                  <DocumentRegular className="text-blue-600" />
                  <div>
                    <Text weight="semibold">{selectedFile.name}</Text>
                    <Text size={200} className="text-gray-600">
                      {formatFileSize(selectedFile.size)}
                    </Text>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    appearance="outline"
                    onClick={handleUploadClick}
                  >
                    Byt fil
                  </Button>
                  <Button
                    size="small"
                    appearance="subtle"
                    onClick={handleRemoveFile}
                  >
                    Ta bort
                  </Button>
                </div>
              </div>
            )}
          </Field>
          
          <Text size={300} className="text-gray-600">
            Accepterade filformat: DOCX (Max 10MB)
          </Text>
          <Table>
            {contractFields.map((field, index) => (
              <TableRow key={index}>
                <div className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-gray-50 group">
                  <div className="flex items-center gap-4">
                    <Text>{field.fieldName}</Text>
                  </div>
                  <Tooltip content="Koppla frågor" relationship="label">
                    <Button
                      appearance="secondary"
                      shape="circular"
                      icon={<LinkFilled />}
                      aria-label={`Koppla frågor till ${field.fieldName}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      onClick={() => {setSelectedField(field); setIsLinkQuestionsDialogOpen(true);}}
                    />
                  </Tooltip>
                </div>
              </TableRow>
            ))}
          </Table>
        </div>
      </Card>
      {selectedField && <LinkQuestionsDialog
        open={isLinkQuestionsDialogOpen}
        onClose={() => {setIsLinkQuestionsDialogOpen(false); setSelectedField(null);}}
        tendering={tenderingDocument}
        field={selectedField}
        onQuestionLinked={handleQuestionLinked}
      />}
    </>
  );
}

export default TenderingContract;